import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useCategoryBySlug, useArticlesByCategory } from "@/hooks/useArticles";
import { ArticleCard } from "@/components/ArticleCard";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ScrollToTop from "@/components/ScrollToTop";
import EmptyState from "@/components/EmptyState";
import { ChevronRight } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Category-specific SEO intro texts
const categoryIntroTexts: Record<string, string> = {
  "sabbataar-intro": "Overvejer du et sabbatår efter gymnasiet eller HF? Her får du en komplet introduktion til, hvad et sabbatår indebærer, hvem det er for, og hvordan du kommer godt i gang med planlægningen.",
  "ideer-inspiration": "Mangler du inspiration til dit sabbatår? Udforsk forskellige muligheder fra backpacking og højskoleophold til frivilligt arbejde og kulturelle oplevelser verden over.",
  "rejse-udland": "Drømmer du om at rejse i dit sabbatår? Find guides til backpacking, working holiday visum, rejseforsikring og praktisk planlægning af din udlandsrejse.",
  "hoejskole-uddannelse": "Overvejer du højskole som en del af dit sabbatår? Lær om forskellige højskoletyper, hvordan du vælger den rigtige, og hvad du kan forvente af opholdet.",
  "arbejde-frivillighed": "Vil du arbejde eller lave frivilligt arbejde i dit sabbatår? Find information om working holiday, au pair, frivilligprogrammer og job i udlandet.",
  "kvote-2": "Vil du bruge dit sabbatår til at styrke din kvote 2-ansøgning? Få tips til aktiviteter og erfaringer, der kan øge dine chancer for at komme ind på drømmestudiet.",
  "su-fremtid": "Hvordan påvirker et sabbatår din SU og fremtidige uddannelse? Få overblik over regler, muligheder og smart planlægning af din økonomi.",
  "oekonomi-planlaegning": "Sådan finansierer du dit sabbatår. Få konkrete tips til budgettering, opsparing, og hvordan du holder styr på økonomien undervejs.",
  "for-foraeldre": "Er dit barn på vej ud i sabbatår? Her finder du guides og råd til forældre om økonomi, sikkerhed, kommunikation og hvordan du bedst støtter dit barn."
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: category, isLoading: categoryLoading } = useCategoryBySlug(slug || "");
  const { data: articles, isLoading: articlesLoading } = useArticlesByCategory(slug || "");

  // Scroll to top when category changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const isLoading = categoryLoading || articlesLoading;

  const colorAccents = ["coral", "marine", "sage", "sky", "lavender"];

  // Get SEO intro text for this category
  const seoIntroText = useMemo(() => {
    if (!slug) return null;
    return categoryIntroTexts[slug] || null;
  }, [slug]);

  // Build breadcrumbs for SEO
  const breadcrumbs = useMemo(() => {
    const items = [{ name: "Forside", url: "https://mysabbat.dk" }];
    if (category) {
      items.push({
        name: category.name,
        url: `https://mysabbat.dk/kategori/${category.slug}`
      });
    }
    return items;
  }, [category]);

  return (
    <>
      <SEOHead
        title={category ? `${category.name} | MySabbat` : "Kategori | MySabbat"}
        description={seoIntroText || category?.description || "Udforsk artikler om sabbatår"}
        path={`/kategori/${slug}`}
        breadcrumbs={breadcrumbs}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <ScrollToTop />
        
        <main className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Forside</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>{category?.name || "Kategori"}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Category header */}
          <div className="mb-8">
            {isLoading ? (
              <>
                <div className="h-8 w-64 bg-muted rounded animate-pulse mb-2" />
                <div className="h-5 w-96 bg-muted rounded animate-pulse" />
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {category?.name}
                </h1>
                {category?.description && (
                  <p className="text-lg text-muted-foreground mb-4">
                    {category.description}
                  </p>
                )}
                {seoIntroText && (
                  <p className="text-muted-foreground leading-relaxed border-l-4 border-primary/30 pl-4 bg-muted/30 py-3 rounded-r-lg">
                    {seoIntroText}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Articles grid */}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card rounded-2xl animate-pulse border border-border">
                  <div className="aspect-[16/10] bg-muted rounded-t-2xl" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 w-20 bg-muted rounded-full" />
                    <div className="h-6 w-3/4 bg-muted rounded" />
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles && articles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, index) => (
                <ArticleCard 
                  key={article.id} 
                  article={article} 
                  colorAccent={colorAccents[index % colorAccents.length]}
                />
              ))}
            </div>
          ) : (
            <EmptyState 
              type="category"
              ctaText="Se alle kategorier"
              ctaLink="/"
            />
          )}

          {/* Back link */}
          <div className="mt-8 pt-8 border-t border-border">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Tilbage til forsiden
            </Link>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
